import { useEffect, useMemo, useState } from 'react';
import { InkBlock } from '@/components/atoms';
import { ChoiceRow, NarrativeTextField } from '@/components/molecules';
import { dwrClassesById } from '@/data/classes';
import type { EquipmentChoiceGroup } from '@/schemas/equipmentChoice';
import type { Item } from '@/schemas/equipment';
import { displayTagsForItem } from '@/utils/itemDisplay';
import { normalizeOptionalTextInput } from '@/utils/textInput';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';
import { inferEquipmentSelectionsFromInventory } from '../equipmentSelections';

/**
 * Step 7 — Equipaggiamento iniziale.
 *
 * Re-design Fase 2 PR 6: `<InkBlock variant="full">` per gli header ("Hai
 * sempre" + ciascun group prompt) + `<ChoiceRow>` per le opzioni di scelta
 * (radio pickCount=1 / checkbox-cap pickCount>1). Items "always" inline come
 * `<ul>` con bullet ink + item name + tags mono opzionali.
 *
 * Stato selections + dispatch SET_INVENTORY invariati dalla Fase 1.
 */
export function SectionEquipment() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;

  const [selections, setSelections] = useState<Record<string, string[]>>(() =>
    inferEquipmentSelectionsFromInventory(
      draft.inventory,
      classDef?.startingEquipment.choices ?? [],
    ),
  );

  // **FI-50** — testo narrativo che il giocatore inserisce per item con
  // `userDescriptionPrompt` (Barbaro Ricordo, Bardo Strumento, Chierico/
  // Druido/Paladino Simbolo sacro). Stato locale keyed by item.id; al
  // dispatch viene mergiato sull'item del computedInventory.
  const [userDescriptions, setUserDescriptions] = useState<Record<string, string>>(() =>
    inferUserDescriptionsFromInventory(draft.inventory),
  );

  const computedInventory = useMemo<Item[]>(() => {
    if (!classDef) return [];
    const always = classDef.startingEquipment.always.map((it) =>
      mergeUserDescription(it, userDescriptions),
    );
    const fromChoices: Item[] = [];
    for (const group of classDef.startingEquipment.choices) {
      const chosen = selections[group.id] ?? [];
      for (const optId of chosen) {
        const opt = group.options.find((o) => o.id === optId);
        if (opt)
          fromChoices.push(...opt.items.map((it) => mergeUserDescription(it, userDescriptions)));
      }
    }
    return [...always, ...fromChoices];
  }, [classDef, selections, userDescriptions]);

  useEffect(() => {
    if (!classDef) return;
    dispatch({ type: 'SET_INVENTORY', inventory: computedInventory });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedInventory]);

  function handleUserDescriptionChange(itemId: string, value: string): void {
    setUserDescriptions((prev) => ({ ...prev, [itemId]: value }));
  }

  if (!classDef) {
    return (
      <p className="text-ink-soft italic">
        Seleziona prima una classe nello step precedente per vedere l'equipaggiamento iniziale.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <AlwaysItemsList
        items={classDef.startingEquipment.always}
        userDescriptions={userDescriptions}
        onUserDescriptionChange={handleUserDescriptionChange}
      />
      {classDef.startingEquipment.choices.map((group) => (
        <ChoiceGroupControl
          key={group.id}
          group={group}
          selected={selections[group.id] ?? []}
          onChange={(next) => setSelections((s) => ({ ...s, [group.id]: next }))}
          userDescriptions={userDescriptions}
          onUserDescriptionChange={handleUserDescriptionChange}
        />
      ))}
    </div>
  );
}

/**
 * **FI-50** — proietta `Item.userDescription` valorizzati dell'inventory
 * salvato in mappa `itemId → text` per inizializzare lo stato locale.
 * Permette di riprendere il builder con testi già scritti.
 */
function inferUserDescriptionsFromInventory(inventory: readonly Item[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const item of inventory) {
    if (item.userDescription !== undefined && item.userDescription.length > 0) {
      map[item.id] = item.userDescription;
    }
  }
  return map;
}

/**
 * **FI-50** — se l'item ha `userDescriptionPrompt` e l'utente ha scritto
 * un testo, lo merge come `userDescription` sull'item che finirà nel
 * draft inventory. Altrimenti restituisce l'item invariato.
 *
 * **BugFix 2026-05-23**: usa `normalizeOptionalTextInput` per check vuoto
 * (whitespace-only). Salva il valore RAW per preservare spazi intermedi
 * e trailing durante typing. Vedi `utils/textInput.ts`.
 */
function mergeUserDescription(item: Item, descriptions: Record<string, string>): Item {
  if (!item.userDescriptionPrompt) return item;
  const raw = descriptions[item.id] ?? '';
  const normalized = normalizeOptionalTextInput(raw);
  if (normalized === null) return item;
  return { ...item, userDescription: normalized };
}

interface AlwaysItemsListProps {
  items: readonly Item[];
  userDescriptions: Record<string, string>;
  onUserDescriptionChange: (itemId: string, value: string) => void;
}

function AlwaysItemsList({
  items,
  userDescriptions,
  onUserDescriptionChange,
}: AlwaysItemsListProps) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <InkBlock variant="full">Hai sempre</InkBlock>
      <ul className="font-serif text-ink m-0 flex list-none flex-col gap-1 p-0 text-[0.92rem]">
        {items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            userDescription={userDescriptions[item.id]}
            onUserDescriptionChange={onUserDescriptionChange}
          />
        ))}
      </ul>
    </div>
  );
}

interface ChoiceGroupControlProps {
  group: EquipmentChoiceGroup;
  selected: string[];
  onChange: (next: string[]) => void;
  userDescriptions: Record<string, string>;
  onUserDescriptionChange: (itemId: string, value: string) => void;
}

function ChoiceGroupControl({
  group,
  selected,
  onChange,
  userDescriptions,
  onUserDescriptionChange,
}: ChoiceGroupControlProps) {
  const isSingle = group.pickCount === 1;
  const headerSuffix = isSingle ? null : (
    <span className="text-ink-soft ml-2 text-xs italic normal-case tracking-normal">
      (scegli {group.pickCount})
    </span>
  );

  function handlePick(optId: string) {
    if (isSingle) {
      onChange([optId]);
      return;
    }
    const isChecked = selected.includes(optId);
    if (isChecked) {
      onChange(selected.filter((id) => id !== optId));
      return;
    }
    if (selected.length >= group.pickCount) return;
    onChange([...selected, optId]);
  }

  // **FI-50** — item con `userDescriptionPrompt` nelle opzioni selezionate.
  // Le textarea sono renderizzate sotto le ChoiceRow per non interferire
  // con il click di selezione opzione.
  const promptedItems: Item[] = [];
  for (const optId of selected) {
    const opt = group.options.find((o) => o.id === optId);
    if (!opt) continue;
    for (const item of opt.items) {
      if (item.userDescriptionPrompt) promptedItems.push(item);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <InkBlock variant="full">
        {group.prompt}
        {headerSuffix}
      </InkBlock>
      <div className="flex flex-col gap-[0.4rem]">
        {group.options.map((opt) => {
          const isSelected = selected.includes(opt.id);
          const isDisabled = !isSingle && !isSelected && selected.length >= group.pickCount;
          return (
            <ChoiceRow
              key={opt.id}
              title={<OptionTitle items={opt.items} />}
              description={<OptionBody items={opt.items} />}
              selected={isSelected}
              onClick={isDisabled ? undefined : () => handlePick(opt.id)}
              className={isDisabled ? 'opacity-50' : undefined}
            />
          );
        })}
      </div>
      {promptedItems.length > 0 ? (
        <div className="mt-1 flex flex-col gap-2 pl-3">
          {promptedItems.map((item) => (
            <ItemUserDescription
              key={item.id}
              item={item}
              value={userDescriptions[item.id] ?? ''}
              onChange={(v) => onUserDescriptionChange(item.id, v)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// page-specific, not reusable: riga di item nella lista "Hai sempre".
// Pattern: name **bold** + tag pill granulari + description italic opzionale
// + (FI-50) textarea user-description se item.userDescriptionPrompt presente.
function ItemRow({
  item,
  userDescription,
  onUserDescriptionChange,
}: {
  item: Item;
  userDescription: string | undefined;
  onUserDescriptionChange: (itemId: string, value: string) => void;
}) {
  return (
    <li className="flex flex-col gap-1 before:content-['•'] before:text-ink before:absolute before:-ml-3 before:font-bold relative pl-1">
      <ItemLine item={item} />
      {item.description ? <ItemDescription text={item.description} /> : null}
      {item.userDescriptionPrompt ? (
        <ItemUserDescription
          item={item}
          value={userDescription ?? ''}
          onChange={(v) => onUserDescriptionChange(item.id, v)}
        />
      ) : null}
    </li>
  );
}

/**
 * **FI-50 + SSOT refactor (2026-05-22)** — campo narrativo del giocatore
 * per un item con `userDescriptionPrompt`. Wrapper del molecule SSOT
 * `<NarrativeTextField>` con valori derivati dal catalogo Item:
 *  - `label` = `item.name` (es. "Ricordo della tua terra")
 *  - `hint` = `item.userDescriptionPrompt` (es. "Descrivilo a tua scelta")
 *  - `required` = true (la descrizione è parte dell'oggetto narrativo)
 *  - `multiline` = true
 */
function ItemUserDescription({
  item,
  value,
  onChange,
}: {
  item: Item;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <NarrativeTextField
      id={`item-userdesc-${item.id}`}
      label={item.name}
      hint={item.userDescriptionPrompt}
      value={value}
      onChange={onChange}
      required
      multiline
      rows={2}
      placeholder={`${item.name}…`}
    />
  );
}

// Pattern uniforme: name bold + qty + tag granulari inline. Non duplica info
// strutturate in description (vedi <ItemDescription>).
function ItemLine({ item }: { item: Item }) {
  const qty = item.quantity && item.quantity > 1 ? ` ×${item.quantity}` : '';
  const tags = displayTagsForItem(item);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <strong className="text-ink font-semibold">
        {item.name}
        {qty}
      </strong>
      {tags.length > 0 ? <TagsInline tags={tags} /> : null}
    </span>
  );
}

// page-specific: descrizione narrativa libera dell'item (es. "+1 danni",
// "Lanciato è ricarica", lore). Mostrata italic sotto i tag.
function ItemDescription({ text }: { text: string }) {
  return <span className="text-ink-soft text-[0.85rem] italic">{text}</span>;
}

// page-specific: titolo di una ChoiceOption derivato dai nomi degli items
// (sostituisce opt.label che spesso duplica item.name). Single → name; multi
// → join " + " per opzioni-bundle (es. "Attrezzatura + Razioni").
function OptionTitle({ items }: { items: readonly Item[] }) {
  return <>{items.map((it) => formatItemHead(it)).join(' + ')}</>;
}

function formatItemHead(item: Item): string {
  const qty = item.quantity && item.quantity > 1 ? ` ×${item.quantity}` : '';
  return `${item.name}${qty}`;
}

// page-specific: body della ChoiceOption — per ogni item, tag pill + eventuale
// description italic. Single → solo tag (no ripetizione name). Multi → ogni
// item su una riga con name bold + tag + description.
function OptionBody({ items }: { items: readonly Item[] }) {
  const [first] = items;
  if (items.length === 1 && first) {
    const tags = displayTagsForItem(first);
    return (
      <span className="flex flex-col gap-1">
        {tags.length > 0 ? <TagsInline tags={tags} /> : null}
        {first.description ? <ItemDescription text={first.description} /> : null}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-1.5">
      {items.map((item, i) => (
        <span key={`${item.id}-${i}`} className="flex flex-col gap-0.5">
          <ItemLine item={item} />
          {item.description ? <ItemDescription text={item.description} /> : null}
        </span>
      ))}
    </span>
  );
}

// Tag pill granulari: un box per ogni attributo (es. "media", "peso 2",
// "+1 danni") invece di un singolo span con separatori. Feedback PR 6
// review: più leggibili e coerenti col pattern manuale-aesthetic delle
// etichette meccaniche (capacità arma, tag mossa).
function TagsInline({ tags }: { tags: readonly string[] }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-1">
      {tags.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className="border-rule bg-paper-deep font-mono text-ink-soft inline-block border px-[0.45em] py-[0.05em] text-[0.7rem] tracking-[0.04em]"
        >
          {tag}
        </span>
      ))}
    </span>
  );
}
